import { useTranslations } from 'next-intl';

import { OrderTypeEnum } from '@flipster/apis/types';
import { OrderSideBadge } from '@flipster/features/src/misc/badge';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { useClosePosition } from '@flipster/features/src/shared/trade-actions';
import { SpotPendingPosition } from '@flipster/r230522/original/spot-order';
import Formatted from '@flipster/react/design-utils/formatted';
import { Button } from '@flipster/ui';
import { TableCell, TableRow } from '@flipster/ui/Table';
import { AssetPrecision, getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { SymbolIconCell } from '@web/app/[locale]/(web)/trade/_misc/etc-section/symbol-icon-cell';

import styles from './positionTable.module.scss';

interface Props {
  item: SpotPendingPosition;
  index: number;
}

const PendingPositionRow = ({ item, index }: Props) => {
  const transAssets = useTranslations('Assets.r250401');
  const { symbol, amount, triggerPrice, qty, price, orderType, leavesQty } = item;

  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const { cancelPosition, isCancelLoading: isLoading } = useClosePosition();

  const instrument = instrumentsKeyBySymbol?.[symbol];

  if (!instrument) return null;
  const isLimitOrder = orderType === OrderTypeEnum['Limit'];
  const limitAmount = Num(leavesQty || 0).multipliedBy(price || 0);

  return (
    <TableRow>
      <SymbolIconCell symbol={symbol} tradeType='spot' index={index} />
      <TableCell>
        <div className={styles.badges}>
          <OrderSideBadge orderSide={item.side} hideIcon />
        </div>
      </TableCell>
      <TableCell>
        {isLimitOrder ? (
          <div className={styles.multiLine}>
            <Formatted.Number
              value={leavesQty}
              precision={AssetPrecision}
              toFixed
              abs
              css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-5' }}
            />
            <Formatted.Number
              value={limitAmount}
              toFixed
              abs
              precision={getQuoteCurrencyPrecision(instrument.quoteCurrency)}
              suffix={instrument.quoteCurrency}
              css={{
                textStyle: 'Text/S/Monospace/Regular',
                color: 'content.default.level-2',
              }}
            />
          </div>
        ) : (
          <div className={styles.multiLine}>
            <Formatted.Number
              value={qty}
              precision={AssetPrecision}
              toFixed
              abs
              css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-5' }}
            />
            <Formatted.Number
              value={amount}
              toFixed
              abs
              precision={getQuoteCurrencyPrecision(instrument.quoteCurrency)}
              suffix={instrument.quoteCurrency}
              css={{
                textStyle: 'Text/S/Monospace/Regular',
                color: 'content.default.level-2',
              }}
            />
          </div>
        )}
      </TableCell>
      <TableCell>
        <Formatted.Number
          value={isLimitOrder ? price : triggerPrice}
          css={{ textStyle: 'Text/S/Monospace/Regular' }}
          precision={instrument.pricePrecision}
          toFixed
        />
      </TableCell>
      <TableCell>
        <Button
          size='small'
          shape='tertiary'
          loading={isLoading}
          className={styles.cancelPosition}
          onClick={() => cancelPosition(item, 'spot')}
        >
          {transAssets('Cancel_order')}
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default PendingPositionRow;
